export type ArticleBlock =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "quote"; text: string };

export interface Article {
  id: string;
  title: string;
  category: string;
  categoryKey: string;
  date: string;
  dateLabel: string;
  summary: string;
  cover: string;
  tags: string[];
  readingTime: number;
  heat: number;
  featured?: boolean;
  body: ArticleBlock[];
}

export interface Category {
  key: string;
  name: string;
  desc: string;
  icon: string;
}

export const categories: Category[] = [
  { key: "variety", name: "综艺热瓜", desc: "综艺名场面与剪辑话题的往期复盘", icon: "ri-tv-2-line" },
  { key: "film", name: "影视观察", desc: "剧集口碑、选角与路透话题存档", icon: "ri-film-line" },
  { key: "creator", name: "创作者动态", desc: "内容创作者与短视频行业现象观察", icon: "ri-video-line" },
  { key: "style", name: "红毯与造型", desc: "盛典红毯、造型复盘与审美讨论", icon: "ri-shirt-line" },
  { key: "industry", name: "行业盘点", desc: "内容产业趋势与年度话题梳理", icon: "ri-bar-chart-2-line" },
  { key: "buzz", name: "网络热议", desc: "社交平台话题走向与舆情回顾", icon: "ri-chat-3-line" },
];

export const tagCloud: string[] = [
  "综艺复盘",
  "路透话题",
  "口碑反转",
  "红毯造型",
  "追星观察",
  "短剧生态",
  "直播带货",
  "年度盘点",
  "话题走向",
  "内容行业",
  "社交热榜",
  "选角讨论",
];

export const articles: Article[] = [
  {
    id: "variety-highlights-2026",
    title: "综艺名场面盘点：那些被剪进年度高光的瞬间",
    category: "综艺热瓜",
    categoryKey: "variety",
    date: "2026-08-28",
    dateLabel: "8月28日",
    summary:
      "从游戏环节到临场反应，这一年的综艺留下了不少被反复二创的片段。我们按播出时间把最出圈的名场面重新捋了一遍，看看哪一类内容最容易成为长尾话题。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20amber%20toned%20abstract%20editorial%20scene%20with%20soft%20stage%20light%20beams%20and%20paper%20texture%20background%2C%20minimal%20artistic%20composition%2C%20no%20people%2C%20warm%20red%20and%20cream%20palette&width=1200&height=800&seq=kl-art-01&orientation=landscape",
    tags: ["综艺复盘", "名场面", "年度盘点"],
    readingTime: 6,
    heat: 9820,
    featured: true,
    body: [
      { type: "p", text: "一档综艺能不能留下记忆点，往往不取决于投资规模，而取决于有没有一个能被反复引用的瞬间。过去一年里，真正被二创反复使用的片段，大多来自临场反应而不是精心设计的桥段。" },
      { type: "h2", text: "一、临场反应比剧本更好看" },
      { type: "p", text: "复盘了全年热度靠前的片段后能发现，观众记住的往往是嘉宾说错话、接不上梗或者瞬间破功的画面。这类内容真实、信息密度高，也更容易被剪成十几秒的短视频传播。" },
      { type: "h3", text: "为什么这类片段更容易出圈" },
      { type: "p", text: "一方面它的理解成本低，不需要前情提要就能看懂；另一方面它带有情绪张力，适合在评论区继续延展讨论。内容创作者在二创时也更愿意选择这类素材。" },
      { type: "quote", text: "能被反复引用的画面，往往不是最贵的，而是最真的。" },
      { type: "h2", text: "二、游戏环节的黄金比例" },
      { type: "p", text: "从数据上看，带有明确规则和胜负结果的中段环节最容易产生高光。规则越清晰，观众越容易代入；胜负越接近，剪辑时的张力就越大。" },
      { type: "h2", text: "三、观众口味正在变化" },
      { type: "p", text: "与前几年相比，纯粹依靠话题度堆砌的剪辑正在失去吸引力。观众更在意内容本身是否完整、嘉宾之间的化学反应是否自然，这也倒逼节目在后期思路上更克制。" },
    ],
  },
  {
    id: "casting-topic-balance",
    title: "选角风波背后：影视剧组如何平衡话题与口碑",
    category: "影视观察",
    categoryKey: "film",
    date: "2026-08-19",
    dateLabel: "8月19日",
    summary:
      "选角消息一出就登上热榜，几乎成了近几年剧集宣发的固定动作。话题度确实能带来首播流量，但真正决定长尾口碑的，仍然是角色与演员的匹配程度。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20terracotta%20and%20cream%20abstract%20film%20strip%20collage%20texture%20background%2C%20artistic%20flat%20lay%2C%20no%20people%2C%20soft%20grain%2C%20editorial%20minimal%20style&width=1200&height=800&seq=kl-art-02&orientation=landscape",
    tags: ["选角讨论", "剧集宣发", "口碑"],
    readingTime: 7,
    heat: 9130,
    featured: true,
    body: [
      { type: "p", text: "选角话题几乎成了剧集开播前的标准预热流程。一段路透、一张定妆照，就足够在社交平台掀起一轮讨论。热度来得快，但对作品本身来说，这只是开始。" },
      { type: "h2", text: "一、话题度是入场券，不是通行证" },
      { type: "p", text: "高话题度的阵容确实能拉高首播关注度，但观众的耐心通常只有两三集。如果角色塑造跟不上，前期积累的讨论会在短时间内反向消耗作品口碑。" },
      { type: "h2", text: "二、匹配度正在被重新重视" },
      { type: "p", text: "越来越多的制作团队开始在开拍前安排围读与试拍，用来验证演员与角色的贴合度。这类流程虽然增加了前期成本，却明显降低了后期返工的风险。" },
      { type: "h3", text: "路透内容的管理" },
      { type: "p", text: "路透是一把双刃剑。适度曝光可以维持热度，过度释放则会让观众在正式播出前就产生审美疲劳。如何把握节奏，考验的是宣发团队的判断力。" },
      { type: "quote", text: "选角的热度只能把观众请进门，留人的还是故事本身。" },
      { type: "h2", text: "三、观众的评价标准更细了" },
      { type: "p", text: "从近年的讨论来看，观众不再只关注番位与流量，而是会具体讨论台词语感、情绪层次与人物逻辑。评价维度的细化，对整个行业其实是件好事。" },
    ],
  },
  {
    id: "word-of-mouth-reversal",
    title: "从路透到正片：口碑反转的剧集为何越来越多",
    category: "影视观察",
    categoryKey: "film",
    date: "2026-08-06",
    dateLabel: "8月6日",
    summary:
      "开播前被唱衰、开播后逆袭，或者反过来高开低走——口碑反转在近两年变得格外常见。这背后既有观众预期管理的因素，也和内容节奏的把控密切相关。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20cream%20and%20muted%20red%20abstract%20layered%20paper%20cut%20composition%2C%20subtle%20gradient%20light%2C%20no%20people%2C%20soft%20editorial%20texture%20background%2C%20minimal%20artistic&width=1200&height=800&seq=kl-art-03&orientation=landscape",
    tags: ["口碑反转", "剧集节奏", "观众预期"],
    readingTime: 6,
    heat: 8640,
    body: [
      { type: "p", text: "口碑反转并不是新现象，但在社交平台放大之下，它出现的频率和幅度都比过去更高。理解反转的成因，比单纯讨论谁对谁错更有价值。" },
      { type: "h2", text: "一、预期管理决定了开局评价" },
      { type: "p", text: "观众在看第一集之前，往往已经通过路透和物料形成了预期。预期越高，前几集的容错率就越低。反过来，被低估的作品只要节奏稳，就容易收获超出预期的评价。" },
      { type: "h2", text: "二、节奏问题集中在前十集" },
      { type: "p", text: "复盘口碑走低的剧集可以发现，问题大多出在中段：支线过散、主线停滞、人物动机模糊。这些都会让追剧的连续性被打断。" },
      { type: "h3", text: "如何判断节奏是否健康" },
      { type: "p", text: "一个简单的观察方法是看每集是否都有一个明确的小目标推进。如果连续三集都没有新的信息量，观众的流失就会开始加速。" },
      { type: "h2", text: "三、反转之后的二次传播" },
      { type: "p", text: "口碑向上反转的作品，往往会迎来一轮新的自来水传播。这种传播比宣发投放更持久，也更能带来长尾播放量。" },
    ],
  },
  {
    id: "creator-boom-cooling",
    title: "探店内容为何突然降温？一份内容行业观察笔记",
    category: "创作者动态",
    categoryKey: "creator",
    date: "2026-07-24",
    dateLabel: "7月24日",
    summary:
      "曾经霸屏的探店类内容，近半年的增长明显放缓。是观众看腻了，还是商业模型走到了瓶颈？我们从内容供给和变现路径两个角度做了梳理。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20amber%20abstract%20wooden%20table%20texture%20with%20soft%20window%20light%20and%20minimal%20prop%20shapes%2C%20no%20people%2C%20cozy%20editorial%20flat%20lay%2C%20cream%20and%20brown%20palette&width=1200&height=800&seq=kl-art-04&orientation=landscape",
    tags: ["探店", "内容变现", "创作者"],
    readingTime: 8,
    heat: 8210,
    body: [
      { type: "p", text: "探店类内容曾经是短视频里最容易复制的赛道之一：场景固定、结构清晰、转化直接。但越是容易复制的模式，越容易在同质化中失去新鲜感。" },
      { type: "h2", text: "一、供给端的同质化" },
      { type: "p", text: "当同一条街出现十几位创作者拍摄高度相似的素材时，观众的注意力会被快速稀释。内容的可替代性增强，单条视频的播放中位数自然下滑。" },
      { type: "h2", text: "二、商业模型的天花板" },
      { type: "p", text: "探店内容的变现主要依赖商家合作与佣金，单价受地域和品类限制明显。当流量成本上升，投入产出比就会变得不再划算。" },
      { type: "h3", text: "哪些方向仍有空间" },
      { type: "p", text: "从数据看，带专业背景的垂直内容（如食材溯源、后厨流程）依然保持增长。深度和可信度正在取代单纯的价格信息，成为新的竞争力。" },
      { type: "quote", text: "当所有人都能拍同一条街时，能讲清为什么的人才有溢价。" },
      { type: "h2", text: "三、从流量到信任的迁移" },
      { type: "p", text: "行业的共识正在形成：观众不排斥商业内容，但排斥不透明的商业内容。明确标注合作、给出可验证的依据，是重建信任的第一步。" },
    ],
  },
  {
    id: "red-carpet-style-review",
    title: "红毯造型复盘：本届盛典的十个高分穿搭思路",
    category: "红毯与造型",
    categoryKey: "style",
    date: "2026-07-11",
    dateLabel: "7月11日",
    summary:
      "红毯从来不只是衣服的展示，更是审美主张的表达。我们按造型语言把本届盛典的亮点归了类，顺便聊聊哪些选择在镜头前更占优势。",
    cover:
      "https://readdy.ai/api/search-image?query=Elegant%20warm%20champagne%20and%20cream%20fabric%20drape%20texture%2C%20soft%20studio%20lighting%2C%20abstract%20luxury%20still%20life%2C%20no%20people%2C%20editorial%20fashion%20mood%20board%20style&width=1200&height=800&seq=kl-art-05&orientation=landscape",
    tags: ["红毯造型", "审美", "盛典"],
    readingTime: 5,
    heat: 7780,
    body: [
      { type: "p", text: "一场盛典结束后，被讨论最多的往往不是奖项，而是造型。造型之所以重要，是因为它需要在几秒钟的镜头里完成一次完整的自我表达。" },
      { type: "h2", text: "一、颜色是第一眼的胜负手" },
      { type: "p", text: "红毯背景通常颜色偏深，因此高饱和或高亮度的造型更容易在画面中跳出来。相反，介于中间灰度的颜色在闪光灯下容易被背景吞掉。" },
      { type: "h2", text: "二、轮廓决定镜头比例" },
      { type: "p", text: "镜头会压缩空间，因此明确的轮廓线比复杂的层次更占优势。利落的肩线、清晰的腰线，往往比繁复的装饰更能撑起画面。" },
      { type: "h3", text: "配饰的加减法" },
      { type: "p", text: "配饰的作用是引导视线，而不是抢占注意力。当服装本身已经有强视觉焦点时，配饰更适合做减法。" },
      { type: "h2", text: "三、风格一致比单品出彩更重要" },
      { type: "p", text: "造型的高级感来自整体协调。发型、妆容、服装与配饰指向同一个主题时，画面才会显得完整而不杂乱。" },
    ],
  },
  {
    id: "fandom-culture-shift",
    title: "追星文化观察：从刷榜应援到理性表达的转变",
    category: "网络热议",
    categoryKey: "buzz",
    date: "2026-06-30",
    dateLabel: "6月30日",
    summary:
      "围绕追星方式的讨论，这几年发生了明显变化。越来越多人开始反感无意义的数字竞赛，转而关注作品本身与理性表达。这份观察记录了转变的轨迹。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20cream%20abstract%20geometric%20shapes%20arranged%20as%20a%20balanced%20grid%2C%20soft%20amber%20light%2C%20no%20people%2C%20minimal%20editorial%20poster%20composition&width=1200&height=800&seq=kl-art-06&orientation=landscape",
    tags: ["追星", "理性表达", "舆情"],
    readingTime: 6,
    heat: 7420,
    body: [
      { type: "p", text: "追星方式的变化，其实是互联网讨论生态变化的一个缩影。当数字指标不再能代表真实喜爱，表达方式自然会寻找新的出口。" },
      { type: "h2", text: "一、数字竞赛的边际效应递减" },
      { type: "p", text: "过去，数据榜单是粉丝组织力的直接体现。但当榜单本身的可信度受到质疑，投入大量精力换来的数字就很难再带来满足感。" },
      { type: "h2", text: "二、表达方式正在迁移" },
      { type: "p", text: "越来越多的讨论转向作品解读、二创剪辑与观后感分享。这类表达门槛更低，也更容易被圈外的人理解。" },
      { type: "h3", text: "理性表达为什么更持久" },
      { type: "p", text: "理性表达不依赖对抗情绪，因此不会随着某一次争论结束而消散。它沉淀下来的是内容本身，而不是一次性的情绪消耗。" },
      { type: "h2", text: "三、平台规则的影响" },
      { type: "p", text: "平台对异常行为的治理，客观上压缩了非理性操作的空间。规则越清晰，讨论就越容易回到内容本身。" },
    ],
  },
  {
    id: "editing-controversy",
    title: "剪辑争议频发：观众到底想看怎样的真实",
    category: "综艺热瓜",
    categoryKey: "variety",
    date: "2026-06-17",
    dateLabel: "6月17日",
    summary:
      "同一段素材，换个剪辑顺序就能讲出完全不同的故事。当剪辑争议越来越多，观众对「真实」的期待也在悄悄改变。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20red%20and%20cream%20abstract%20editing%20timeline%20and%20film%20frame%20texture%20collage%2C%20soft%20studio%20light%2C%20no%20people%2C%20editorial%20minimal%20design&width=1200&height=800&seq=kl-art-07&orientation=landscape",
    tags: ["剪辑", "综艺", "真实性"],
    readingTime: 5,
    heat: 6980,
    body: [
      { type: "p", text: "剪辑是综艺的第二次创作，它决定了观众最终看到的是哪个版本的故事。当剪辑手法被越来越多观众识别出来，争议也就随之出现。" },
      { type: "h2", text: "一、叙事需求与真实性之间的张力" },
      { type: "p", text: "节目需要完整的故事线和情绪起伏，这与素材的原始状态天然存在差距。问题不在于是否剪辑，而在于是否保留了基本的事实逻辑。" },
      { type: "h2", text: "二、观众变得更有识别力" },
      { type: "p", text: "随着花絮、直播和路透内容的增多，观众有了更多交叉验证的渠道。一旦正片与公开素材出现明显出入，讨论就会迅速发酵。" },
      { type: "quote", text: "观众不要求完全未经修饰，但要求逻辑经得起推敲。" },
      { type: "h2", text: "三、透明的沟通更重要" },
      { type: "p", text: "面对争议，及时、具体的说明往往比沉默更有效。把创作意图讲清楚，反而能减少误读的空间。" },
    ],
  },
  {
    id: "short-drama-next",
    title: "短剧爆火之后：内容行业的下一个机会在哪里",
    category: "行业盘点",
    categoryKey: "industry",
    date: "2026-06-02",
    dateLabel: "6月2日",
    summary:
      "短剧用极快的节奏验证了内容效率的重要性。当热度进入平稳期，行业开始思考：这套方法论还能迁移到哪些场景？",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20amber%20abstract%20city%20skyline%20made%20of%20paper%20cut%20layers%2C%20soft%20golden%20hour%20light%2C%20no%20people%2C%20minimal%20editorial%20poster%20style&width=1200&height=800&seq=kl-art-08&orientation=landscape",
    tags: ["短剧", "内容行业", "趋势"],
    readingTime: 7,
    heat: 6640,
    body: [
      { type: "p", text: "短剧最值得被研究的地方，不是它有多火，而是它把内容效率这件事推到了极致：三秒抓人、三十秒反转、三分钟一个完整闭环。" },
      { type: "h2", text: "一、效率方法论的三条原则" },
      { type: "p", text: "第一，开场即冲突；第二，信息密度高、不留废笔；第三，节奏稳定，每一集都给出明确回报。这三点并不依赖题材，而是可迁移的结构能力。" },
      { type: "h2", text: "二、向上游延伸的可能" },
      { type: "p", text: "短剧积累的节奏经验，正在被长视频与品牌内容借鉴。把短剧的抓人能力，与长内容的深度结合起来，是不少团队正在尝试的方向。" },
      { type: "h3", text: "注意不要过度消耗" },
      { type: "p", text: "当同类内容供给过剩，观众的耐受度会下降。过度依赖套路化反转，最终会让整个品类失去信任。" },
      { type: "h2", text: "三、机会藏在细分需求里" },
      { type: "p", text: "与其在最大的赛道里内卷，不如去寻找被忽略的细分人群。越是具体的需求，越容易被稳定的内容供给满足。" },
    ],
  },
  {
    id: "livestream-recovery",
    title: "直播间里的信任危机：带货口碑如何修复",
    category: "创作者动态",
    categoryKey: "creator",
    date: "2026-05-21",
    dateLabel: "5月21日",
    summary:
      "一次选品失误，可能抵消长期积累的信任。复盘几起典型的直播争议后，我们整理出内容电商修复口碑的几个关键动作。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20terracotta%20abstract%20studio%20microphone%20and%20softbox%20light%20shapes%2C%20no%20people%2C%20cozy%20editorial%20flat%20lay%2C%20cream%20background%2C%20minimal&width=1200&height=800&seq=kl-art-09&orientation=landscape",
    tags: ["直播带货", "信任", "选品"],
    readingTime: 6,
    heat: 6310,
    body: [
      { type: "p", text: "内容电商的核心资产不是流量，而是信任。流量可以买，信任只能一点点积累，却可能因为一次选品问题迅速流失。" },
      { type: "h2", text: "一、问题往往出在供应链" },
      { type: "p", text: "复盘案例可以看到，多数争议并非源于主播本意，而是选品与品控环节的疏漏。前端话术再漂亮，也无法弥补产品本身的缺陷。" },
      { type: "h2", text: "二、回应速度决定影响范围" },
      { type: "p", text: "出现问题时，第一时间给出明确的解决方案，通常能显著降低负面影响。拖延和模糊回应，反而会把讨论推向更不利的方向。" },
      { type: "h3", text: "可操作的三步" },
      { type: "p", text: "第一步，暂停相关商品并在显眼位置说明；第二步，给出可执行的退换方案；第三步，公开后续的选品改进措施。" },
      { type: "h2", text: "三、把透明变成长期资产" },
      { type: "p", text: "主动公开选品标准与质检流程，短期内会增加成本，长期看却是最有效的信任背书。" },
    ],
  },
  {
    id: "annual-topic-review",
    title: "年度娱乐盘点：十件事看懂这一年的话题走向",
    category: "行业盘点",
    categoryKey: "industry",
    date: "2026-05-08",
    dateLabel: "5月8日",
    summary:
      "把时间线拉长来看，今年的娱乐话题呈现出几个清晰的走向：从情绪驱动转向内容驱动，从单点爆款转向长线运营。这份盘点帮你一次看懂。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20cream%20and%20deep%20red%20abstract%20annual%20calendar%20grid%20texture%20with%20soft%20grain%2C%20no%20people%2C%20editorial%20minimal%20poster%20composition&width=1200&height=800&seq=kl-art-10&orientation=landscape",
    tags: ["年度盘点", "话题走向", "趋势"],
    readingTime: 9,
    heat: 5920,
    body: [
      { type: "p", text: "单个事件容易被情绪放大，但把一整年的时间线连起来看，规律就会浮出水面。这份盘点试图从纷杂的讨论中，抽出几条真正有意义的线索。" },
      { type: "h2", text: "一、从情绪驱动到内容驱动" },
      { type: "p", text: "纯粹依靠情绪对抗的话题，生命周期普遍在几天到两周之间。相比之下，围绕作品本身展开的讨论，热度曲线更平缓，但持续时间更长。" },
      { type: "h2", text: "二、长线运营取代单点爆款" },
      { type: "p", text: "越来越多的团队开始把一次宣发拆成多个节奏点，用持续的内容供给维持关注度。这种打法对内容储备的要求更高，但结果更可控。" },
      { type: "h3", text: "数据之外还要看什么" },
      { type: "p", text: "播放量与互动量只是结果指标。更值得关注的，是讨论中正面与中性表达的比例，以及话题结束后是否留下了新的内容资产。" },
      { type: "quote", text: "真正留下来的，从来不是最吵的那件事，而是最经得起回看的那件。" },
      { type: "h2", text: "三、观众审美在升级" },
      { type: "p", text: "从全年的讨论来看，观众对套路的识别速度明显加快。真诚的表达、扎实的内容，正在获得比技巧更高的回报。" },
      { type: "h2", text: "四、给内容创作者的三点提示" },
      { type: "p", text: "第一，把注意力放在可积累的内容资产上；第二，不要高估单次热度的价值；第三，尊重观众的判断力，不要依赖信息差。" },
    ],
  },
];

export const getArticleById = (id: string): Article | undefined =>
  articles.find((item) => item.id === id);

export const getArticlesByCategory = (key: string): Article[] =>
  articles.filter((item) => item.categoryKey === key);

export const hotArticles = (limit = 5): Article[] =>
  [...articles].sort((a, b) => b.heat - a.heat).slice(0, limit);

export const relatedArticles = (current: Article, limit = 3): Article[] =>
  articles.filter((item) => item.id !== current.id && item.categoryKey === current.categoryKey).slice(0, limit);

export const latestArticles = (limit = 8): Article[] =>
  [...articles].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, limit);